import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { toast } from "sonner";
import { delftBlue, flame } from "@/theme/colors";
import AllFormsTable from "./components/AllFormsTable";
import ActivitiesPagination from "./components/ActivitiesPagination";
import {
  useGetAllFormSubmissionsQuery,
  useGetActivitiesQuery,
  useDownloadFormSubmissionsBulkMutation,
} from "@/app/features/dashboard/dashboardApiSlice";
import { BULK_DOWNLOAD_MAX } from "@/constants/forms";
import {
  getDownloadErrorMessage,
  getFormSubmissionRef,
  getDeviceTimeZone,
  parseContentDispositionFilename,
  saveBlobFile,
} from "@/utils/form-download";

const AllFormsPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    activityId: "",
    guideName: "",
    formType: "all",
  });
  const [selected, setSelected] = useState({});
  const [downloadMode, setDownloadMode] = useState(null);

  const {
    data: submissionsResponse,
    isLoading: isLoadingSubmissions,
    error: submissionsError,
  } = useGetAllFormSubmissionsQuery({
    page,
    limit,
    ...filters,
  });

  const { data: activitiesResponse } = useGetActivitiesQuery({
    page: 1,
    limit: 1000,
  });

  const [downloadFormSubmissionsBulk] = useDownloadFormSubmissionsBulkMutation();

  const submissions = submissionsResponse?.data?.submissions || [];
  const pagination = submissionsResponse?.data?.pagination || {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: limit,
    hasNextPage: false,
    hasPrevPage: false,
  };

  const activities = activitiesResponse?.data?.activities || activitiesResponse?.data || [];
  const selectedItems = Object.values(selected);
  const selectedCount = selectedItems.length;
  const isDownloading = Boolean(downloadMode);

  useEffect(() => {
    setSelected({});
  }, [filters]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleView = (submission) => {
    const formType = submission.formType || "kayak";
    const submissionId = submission._id || submission.id;
    navigate(`/all-forms/${formType}/${submissionId}`);
  };

  const handleToggleOne = (submission) => {
    const ref = getFormSubmissionRef(submission);
    if (!ref.id) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[ref.key]) {
        delete next[ref.key];
      } else {
        next[ref.key] = { formType: ref.formType, id: ref.id };
      }
      return next;
    });
  };

  const handleTogglePage = (checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      submissions.forEach((submission) => {
        const ref = getFormSubmissionRef(submission);
        if (!ref.id) return;
        if (checked) {
          next[ref.key] = { formType: ref.formType, id: ref.id };
        } else {
          delete next[ref.key];
        }
      });
      return next;
    });
  };

  const saveBulkDownload = async (body) => {
    const timeZone = getDeviceTimeZone();
    const payload = await downloadFormSubmissionsBulk({
      ...body,
      ...(timeZone ? { timeZone } : {}),
    }).unwrap();
    if (!payload?.blob) {
      throw new Error(payload?.message || "Failed to download form submissions");
    }
    const filename =
      parseContentDispositionFilename(payload.filename) ||
      "form-submissions.zip";
    saveBlobFile(payload.blob, filename);
    toast.success("Forms downloaded");
  };

  const handleDownloadSelected = async () => {
    if (!selectedCount) {
      toast.error("Select at least one form to download");
      return;
    }
    if (selectedCount > BULK_DOWNLOAD_MAX) {
      toast.error(
        `You can download at most ${BULK_DOWNLOAD_MAX} forms at a time`,
      );
      return;
    }
    setDownloadMode("selected");
    try {
      await saveBulkDownload({ items: selectedItems });
    } catch (error) {
      toast.error(await getDownloadErrorMessage(error));
    } finally {
      setDownloadMode(null);
    }
  };

  const handleDownloadAll = async () => {
    const total = pagination.totalItems || 0;
    if (!total) {
      toast.error("No form submissions to download");
      return;
    }
    if (total > BULK_DOWNLOAD_MAX) {
      toast.error(
        `Too many matching forms (${total}). Narrow the filters or select up to ${BULK_DOWNLOAD_MAX} forms.`,
      );
      return;
    }
    setDownloadMode("all");
    try {
      const body = {
        selectAllMatchingFilters: true,
        formType: filters.formType || "all",
      };
      if (filters.startDate) body.startDate = filters.startDate;
      if (filters.endDate) body.endDate = filters.endDate;
      if (filters.activityId) body.activityId = filters.activityId;
      if (filters.guideName) body.guideName = filters.guideName;
      await saveBulkDownload(body);
    } catch (error) {
      toast.error(await getDownloadErrorMessage(error));
    } finally {
      setDownloadMode(null);
    }
  };

  const downloadButtonSx = {
    borderRadius: 2,
    textTransform: "none",
    fontFamily: "Montserrat, sans-serif",
    whiteSpace: "nowrap",
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ alignSelf: "stretch" }}>
        <Box
          sx={{
            alignSelf: "stretch",
            minHeight: { xs: "auto", sm: 60 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: 331.6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: { xs: 0.5, sm: 0.75, md: 1 },
            }}
          >
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 22, sm: 24, md: 28 },
                fontFamily: "Audiowide, sans-serif",
                fontWeight: 400,
                lineHeight: 1.29,
                width: "100%",
              }}
            >
              Submitted Forms
            </Typography>
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 13, sm: 14, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 400,
                lineHeight: 1.5,
                width: "100%",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              View and manage all form submissions from your company users
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={
                downloadMode === "selected" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FileDownloadIcon />
                )
              }
              onClick={handleDownloadSelected}
              disabled={isDownloading || !selectedCount}
              sx={{
                ...downloadButtonSx,
                borderColor: delftBlue[200],
                color: delftBlue[500],
                "&:hover": {
                  borderColor: delftBlue[300],
                  background: delftBlue[50],
                },
              }}
            >
              {selectedCount
                ? `Download selected (${selectedCount})`
                : "Download selected"}
            </Button>
            <Button
              variant="contained"
              startIcon={
                downloadMode === "all" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FileDownloadIcon />
                )
              }
              onClick={handleDownloadAll}
              disabled={
                isDownloading || isLoadingSubmissions || !pagination.totalItems
              }
              sx={{
                ...downloadButtonSx,
                backgroundColor: flame[500],
                "&:hover": {
                  backgroundColor: flame[600],
                },
              }}
            >
              Download all
            </Button>
          </Stack>
        </Box>

        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, sm: 4, md: 6 },
          }}
        >
          {isLoadingSubmissions ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <CircularProgress />
            </Box>
          ) : submissionsError ? (
            <Alert severity="error">
              {submissionsError?.data?.message ||
                "Failed to load form submissions"}
            </Alert>
          ) : (
            <>
              <AllFormsTable
                submissions={submissions}
                onView={handleView}
                filters={filters}
                onFilterChange={handleFilterChange}
                activities={activities}
                isLoading={isLoadingSubmissions}
                selected={selected}
                onToggleOne={handleToggleOne}
                onTogglePage={handleTogglePage}
              />

              <ActivitiesPagination
                page={pagination.currentPage || page}
                limit={limit}
                total={pagination.totalItems || 0}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default AllFormsPage;
